<template>
    <div>
        <v-scale-transition>
            <title-card title="Age ranges" help="data-variables-age-ranges" :loading="loading">

                <div class="d-flex">

                    <v-btn color="primary" class="mb-2 align-self-center" @click="addRange">
                        Add new age range
                    </v-btn>

                    <v-scale-transition>
                        <v-btn v-show="dataVariable && dataVariable.id && ranges.length > 0"
                               color="primary"
                               class="ml-2 mb-2 align-self-center"
                               @click="getCountsClicked">Analyze
                        </v-btn>
                    </v-scale-transition>

                    <v-scale-transition>
                        <div class="align-self-center flex-grow-1 ml-7" v-show="excludedCount > 0">
                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <v-progress-linear
                                        v-on="on"
                                        :value="excludedPercent"
                                        color="red"
                                        height="25"
                                        class="mb-2"
                                        style="max-width: 400px;"
                                    >
                                        <template v-slot:default="{ value }">
                                            <strong>Excluded {{ Math.round(value) }}%</strong>
                                        </template>
                                    </v-progress-linear>
                                </template>
                                <span>
                                        {{ excludedCount.toLocaleString() }} records do not match any of the currently defined ranges!
                                    </span>
                            </v-tooltip>
                        </div>
                    </v-scale-transition>
                </div>

                <v-card color="transparent" flat v-for="range in ranges" :key="range.key" class="d-flex pt-1">

                    <v-card flat class="align-self-center" width="300">
                        <v-text-field v-model="range.name"
                                      class="pa-0"
                                      dense
                                      hide-details
                                      outlined
                                      @input="validateRanges"
                        ></v-text-field>
                    </v-card>

                    <v-card flat class="align-self-center" max-width="100">
                        <v-text-field v-model="range.start"
                                      class="pa-0"
                                      dense
                                      hide-details
                                      outlined
                                      @input="validateRanges"
                        ></v-text-field>
                    </v-card>

                    <div class="align-self-center pa-2">
                        TO
                    </div>

                    <!-- RANGE NAME -->
                    <v-card flat class="align-self-center" max-width="100">
                        <v-text-field v-model="range.end"
                                      class="pa-0"
                                      dense
                                      hide-details
                                      outlined
                                      @input="validateRanges"
                        ></v-text-field>
                    </v-card>

                    <!-- RANGE STATS -->
                    <div class="align-self-center pl-4" style="width: 200px" v-if="range.count!==null">
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-progress-linear
                                    v-on="on"
                                    :value="range.percent"
                                    color="primary"
                                    height="25"
                                >
                                    <template v-slot:default="{ value }">
                                        <strong>{{ Math.round(value) }}%</strong>
                                    </template>
                                </v-progress-linear>
                            </template>
                            <span>Count {{ range.count.toLocaleString() }}</span>
                        </v-tooltip>
                    </div>

                    <!-- DELETE RANGE -->
                    <v-btn icon class="align-self-center" @click="deleteRangeClicked(range.key)">
                        <v-icon color="grey lighten-2">mdi-delete</v-icon>
                    </v-btn>

                    <!-- RANGE ERROR -->
                    <v-alert v-show="range.error"
                             dense
                             style="border: 1px solid yellow"
                             class="align-self-center mb-0 pl-2 pr-2 pt-1 pb-1 body-2"
                             type="error">{{ range.error }}
                    </v-alert>

                </v-card>
            </title-card>
        </v-scale-transition>

        <confirmation-dialog v-model="deleteConfirmationDialogModel"
                             headline="Are you sure you?"
                             :description="deleteDescription"
                             delete
                             @selected="deleteRange"
        ></confirmation-dialog>

        <json v-if="false" :data="ranges"></json>

    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import TitleCard from "../../../components/UI/TitleCard";
import {v4 as uuid} from 'uuid';
import {mapActions} from 'vuex';
import Store from "../store";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";

const vuexModule = 'DataVariables'

export default {

    components: {
        Json,
        TitleCard,
        ConfirmationDialog
    },

    props: {
        dataVariable: {
            type: Object | null,
            required: true,
        },
    },

    data() {
        return {
            variable: null,
            ranges: [],
            totalCount: 0,
            excludedPercent: 0,
            excludedCount: 0,
            loading: false,
            rangeUnderDelete: null,
            deleteConfirmationDialogModel: false,
            deleteDescription:
                "Deleting this range can produce undesired results if the range " +
                "is currently been used in any segments or filters." +
                "Please ensure that this range is not longer used anywhere.",
        }
    },

    // watch: {
    //     // Async loading watch
    //     dataVariable(variable) {
    //         this.prepareVariable(variable);
    //     }
    // },

    mounted() {
        // If user is coming from the list page (both props are populated)
        if (this.dataVariable) {
            this.prepareVariable(this.dataVariable);
        }
    },

    methods: {

        ...mapActions(vuexModule, [
            'getRangeStats',
        ]),

        getCountsClicked() {

            this.loading = true;

            this.getRangeStats({ranges: this.ranges, dataVariableId: this.variable.id})
                .then(response => {

                    this.totalCount = response.data.total;
                    this.excludedCount = response.data.excludedCount;
                    this.excludedPercent = response.data.excludedPercent;

                    response.data.ranges.forEach(range => {
                        let found = null;

                        if (range.id) found = this.ranges.find(r => r.id === range.id);
                        if (range.key) found = this.ranges.find(r => r.key === range.key);

                        if (found) {
                            found.count = range.count;
                            found.percent = range.percent;
                        }
                    });
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        validateRanges() {

            let start = null;
            let end = null;

            this.ranges.forEach(range => {

                range.error = null;

                if (!Number.isInteger(parseInt(range.start))) {
                    range.error = "Invalid start value";
                }

                if (!Number.isInteger(parseInt(range.end))) {
                    range.error = "Invalid end value";
                }

                if (!range.error && range.start >= range.end) {
                    range.error = "Start age must be less than end age";
                }

                if (!range.error && start !== null) {
                    if (parseInt(range.start) > (end + 1)) {
                        range.error = "Gap detected";
                    }
                }

                if (!range.error && start !== null) {
                    if (parseInt(range.start) < (end + 1)) {
                        range.error = "Overlap detected";
                    }
                }

                start = parseInt(range.start);
                end = parseInt(range.end);
            })

            this.emit();
        },

        addRange() {

            let step = 7;
            let start = 18;

            if (this.ranges.length > 0) {
                const firstRange = this.ranges[0];
                const lastRange = this.ranges[this.ranges.length - 1];

                step = parseInt(firstRange.end) - parseInt(firstRange.start);
                start = parseInt(lastRange.end) + 1;
            }

            const range = {
                key: uuid(),
                start: start,
                end: start + step,
                name: 'Ages ' + start + ' to ' + (start + step),
                error: null,
                count: null,   // Must add this to stay reactive
                percent: null, // Must add this to stay reactive
            }

            this.ranges.push(range);

            this.validateRanges();
            this.emit();
        },

        deleteRangeClicked(key) {
            this.deleteConfirmationDialogModel = true;
            this.rangeUnderDelete = key;
        },

        deleteRange(bool) {

            if (bool) {

                const index = this.ranges.findIndex(range => range.key === this.rangeUnderDelete);

                if (index !== null) this.ranges.splice(index, 1);

                this.validateRanges();
                this.emit();
            }
        },

        emit() {

            this.variable.ranges = this.ranges.map(range => {
                let cleaned = {
                    name: range.name,
                    start: range.start,
                    end: range.end
                }

                // If ID numeric send it
                if (!isNaN(range.id)) {
                    cleaned.id = range.id;
                }

                return cleaned;
            });

            this.$emit('change', this.variable);
        },

        prepareVariable() {
            if (this.dataVariable) {

                this.variable = JSON.parse(JSON.stringify(this.dataVariable));

                if (this.variable.ranges) {

                    this.ranges = this.variable.ranges.map(range => {
                        let cleaned = {
                            key: uuid(),
                            name: range.name,
                            start: range.start,
                            end: range.end,
                            count: null,
                            error: null,
                        }

                        if (range.id) {
                            cleaned.id = range.id
                        }

                        return cleaned;
                    });
                }

                this.validateRanges();
            }
        }
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    }

}
</script>
